/**
 * Y3 REGISTRY: the governed agent catalog, as code. One entry per canvas agent (the seven in `CANVAS_AGENTS`,
 * agentRoster.ts). The registry describes; it never decides. Ids, owners and duties come from the roster
 * (`AGENT_ID_OWNER`, `DUTIES`); event kinds come from the event catalog (`EVENT_CATALOG` emitters). Those
 * derived facts are pinned by `agentRegistry.test.ts`, so a drift between this file and the roster or the
 * catalog is a red test, not a silent second copy.
 *
 * T3 (CLAUDE.md section 3): this file is a human-edited constant. No agent writes it. The rule for changing a
 * version is in docs/agent-registry.md: a version bump edits this file in the same commit.
 *
 * CLAUDE.md section 5: every event row carries agent_id and agent version, so the registry's `auditQuery`
 * is the filter the admin page uses to read one agent's rows back.
 */
import { deepFreeze } from "./deepFreeze";
import type { CanvasAgentId, DutyId } from "./agentRoster";

export type RegistryOwner = "owner" | "ackra";

/**
 * What the agent may do, in the order of the write tiers (CLAUDE.md section 3): reads only, drafts that need a
 * human approval (T2), writes that ship after floors and evals (T1), or anything on the send path (section 8).
 */
export type RiskPosture = "reads_only" | "drafts_t2" | "writes_t1" | "touches_send_path";

export interface RegistryEnvironments {
  readonly dev: boolean;
  readonly staging: boolean;
  readonly prod: boolean;
}

export interface RegistryEntry {
  readonly id: CanvasAgentId;
  readonly name: string;
  readonly owner: RegistryOwner;
  /** The canvas agent's own version. Per-identity stamped versions are in STAMPED_VERSION_LITERALS. */
  readonly version: string;
  readonly riskPosture: RiskPosture;
  readonly environments: RegistryEnvironments;
  readonly duties: readonly DutyId[];
  /** Every AGENT_ID_OWNER id this canvas agent owns. Exactly one entry owns each id. */
  readonly agentIds: readonly string[];
  /** The LIVE task kinds this agent's handlers serve (taskKinds.ts). */
  readonly taskKinds: readonly string[];
  /** The catalog kinds its agentIds emit (eventCatalog.ts emitters). Derived, pinned by the test. */
  readonly eventKinds: readonly string[];
  /** The ACKRA_ENABLE_* (or ACKRA_LEARN_*) switches that gate this agent. Each defaults to off. */
  readonly flags: readonly string[];
  readonly auditQuery: { readonly agentIds: readonly string[] };
}

/**
 * Every version literal the production code stamps on an event, alert or draft, as the test scans them. A
 * handler that bumps a literal without this list changing fails `agentRegistry.test.ts`.
 */
export const STAMPED_VERSION_LITERALS: readonly string[] = deepFreeze<string[]>(["0.1.0", "0.2.0", "0.3.0", "1", "llm-v1", "onboard-0.1.0"]); // onboard-0.1.0: Y2 ONBOARD, the autolearn duty autolearn.onboard stamps a duty-scoped version (core/onboarding.ts ONBOARD_AGENT)

const ENTRIES: readonly RegistryEntry[] = [
  {
    id: "research",
    name: "Research",
    owner: "ackra",
    version: "0.1.0",
    riskPosture: "reads_only",
    environments: { dev: true, staging: false, prod: false },
    duties: [],
    agentIds: ["research", "research.enrichment"],
    taskKinds: ["lead.research"],
    eventKinds: ["lead.research.ran", "lead.researched", "research.lookup_called", "vip.detected"],
    flags: ["ACKRA_ENABLE_RESEARCH"],
    auditQuery: { agentIds: ["research", "research.enrichment"] },
  },
  {
    id: "reputation",
    name: "Reputation",
    owner: "owner",
    version: "0.1.0",
    riskPosture: "drafts_t2",
    environments: { dev: true, staging: false, prod: false },
    duties: [],
    agentIds: ["reputation", "review_ask_sweep"],
    taskKinds: ["review.cycle"],
    eventKinds: [
      "content.suggested",
      "review.cycle.ran",
      "review.heard_requested",
      "review.ingested",
      "review.response_drafted",
      "review.response_held",
      "review.synthesis_reported",
    ],
    flags: ["ACKRA_ENABLE_REVIEW_CYCLE"],
    auditQuery: { agentIds: ["reputation", "review_ask_sweep"] },
  },
  {
    id: "shepherd",
    name: "Shepherd",
    owner: "owner",
    version: "0.1.0",
    riskPosture: "drafts_t2",
    environments: { dev: true, staging: false, prod: false },
    duties: [],
    agentIds: ["follow_up_sweep", "followups", "shepherd", "shepherd-stages"],
    taskKinds: ["pipeline.stage_watch", "pipeline.sweep"],
    eventKinds: [
      "lead.owner_assigned",
      "order.delivered",
      "pipeline.call_list_built",
      "pipeline.hygiene_reported",
      "pipeline.sweep.ran",
      "reactivation.proposed",
      "reactivation.suppressed",
      "stage.changed",
      "stage.not_derivable",
      "stage.sla_breached",
    ],
    flags: ["ACKRA_ENABLE_SHEPHERD", "ACKRA_ENABLE_STAGE_WATCH"],
    auditQuery: { agentIds: ["follow_up_sweep", "followups", "shepherd", "shepherd-stages"] },
  },
  {
    id: "analyst",
    name: "Analyst",
    owner: "owner",
    version: "0.1.0",
    riskPosture: "reads_only",
    environments: { dev: true, staging: false, prod: false },
    duties: [],
    agentIds: ["amber_digest", "analyst", "outcome_digest", "scoreboard"],
    // TA-1336: competitor.extract records the competitor mentions customers make in DMs, for the owner's report.
    taskKinds: ["analyst.weekly", "analyst.daily", "analyst.ask", "scoreboard.weekly", "competitor.extract"],
    eventKinds: [
      "analyst.answered",
      "analyst.ask.ran",
      "analyst.asked",
      "analyst.demand_spike",
      "analyst.persona_halted",
      "analyst.premium_required",
      "analyst.slot_claimed",
      "analyst.weekly.ran",
      "analyst.daily.ran",
      "analyst.personas",
      "digest.briefing",
      "digest.analyst",
      "digest.delivered",
      "scoreboard.reported",
    ],
    flags: ["ACKRA_ENABLE_ANALYST", "ACKRA_ENABLE_COMPETITOR_INTEL"],
    auditQuery: { agentIds: ["amber_digest", "analyst", "outcome_digest", "scoreboard"] },
  },
  {
    id: "nurture",
    name: "Nurture",
    owner: "owner",
    version: "0.1.0",
    riskPosture: "touches_send_path",
    environments: { dev: true, staging: false, prod: false },
    duties: ["nurture.reply", "nurture.qualify", "nurture.schedule"],
    agentIds: [
      "appointment_reminder_sweep",
      "booking",
      "catalog",
      "consult-intent",
      "escalation_sla",
      "memory-save-gate",
      "missed_call_text_back",
      "qualify",
      "rail",
      "reply",
      "reply.transmit",
      "routing",
      "scheduler",
      "suppress",
      "team_route",
      "vip_alert",
    ],
    taskKinds: [
      "reply.compose",
      "reply.finalize",
      "reply.transmit",
      "reply.proactive",
      "memory.save_gate",
      "consult.intent.detect",
      "consult.book",
      "consult.brief",
      "consult.summary",
      "lead.score",
      "suppression.detect",
      "catalog.feed_sync",
      "media.meta_sync",
      "media.vision_tag",
      "rail.prefill",
      "team.route",
    ],
    eventKinds: [
      "catalog.imported",
      "consult.book.ran",
      "consult.brief.ran",
      "consult.brief_ready",
      "consult.confirmation_sent",
      "consult.proposed",
      "consult.summary.ran",
      "consult.summary_written",
      "experiment.assigned",
      "lead.scored",
      "media.synced",
      "rail.prefilled",
      "reactivation.deferred",
      "reactivation.drafted",
      "reply.proactive.ran",
      "source.asked",
      "suppression.added",
      "suppression.suggested",
      "thread.assigned",
    ],
    flags: [
      "ACKRA_ENABLE_TRANSMIT",
      "ACKRA_ENABLE_LEAD_SCORE",
      "ACKRA_ENABLE_CONSULT_INTENT",
      "ACKRA_ENABLE_CONSULT_BOOK",
      "ACKRA_ENABLE_MEMORY_WRITES",
      "ACKRA_ENABLE_SUPPRESSION_DETECT",
      "ACKRA_ENABLE_CATALOG_SYNC",
      "ACKRA_ENABLE_RAIL_PREFILL",
      "ACKRA_ENABLE_TEAM_ROUTE",
    ],
    auditQuery: {
      agentIds: [
        "appointment_reminder_sweep",
        "booking",
        "catalog",
        "consult-intent",
        "escalation_sla",
        "memory-save-gate",
        "missed_call_text_back",
        "qualify",
        "rail",
        "reply",
        "reply.transmit",
        "routing",
        "scheduler",
        "suppress",
        "team_route",
        "vip_alert",
      ],
    },
  },
  {
    id: "autolearn",
    name: "Auto-learn",
    owner: "ackra",
    version: "0.1.0",
    riskPosture: "drafts_t2",
    environments: { dev: true, staging: false, prod: false },
    duties: ["autolearn.onboard"],
    agentIds: ["autolearn", "ghost_labeler", "graduation", "lessons", "signals"],
    taskKinds: ["learn.weekly", "learn.monitor", "lessons.write", "signal.attribute", "graduation.review", "onboard.run"], // onboard.run: Y2 ONBOARD
    eventKinds: [
      "artifact.auto_reverted",
      "artifact.revert_flagged",
      "eval.refresh_proposed",
      "memory.prune_proposed",
      "attribution.stamped",
      "autonomy.changed",
      "correction.attributed",
      "graduation.review.ran",
      "hypothesis.evaluated",
      "hypothesis.registered",
      "learn.batch_finished",
      "learn.batch_started",
      "learn.halted",
      "learn.truncated",
      "lesson.promoted",
      "lesson.proposed",
      "lesson.pruned",
      "lesson.superseded",
      "onboard.agenda_built",
      "onboard.completed",
      "onboard.halted",
      "onboard.started",
      "onboard.step",
      "owner.note.status",
      "owner.reverse_grill",
      "pattern.retracted",
      "pattern.self_retracted",
      "proposal.queued",
      "proposal.status",
      "signal.edit",
      "signal.invisible_failure",
    ],
    flags: ["ACKRA_LEARN_LABELER", "ACKRA_ENABLE_GRADUATION_REVIEW", "ACKRA_ENABLE_ONBOARD"],
    auditQuery: { agentIds: ["autolearn", "ghost_labeler", "graduation", "lessons", "signals"] },
  },
  {
    id: "campaign",
    name: "Campaign operator",
    owner: "owner",
    version: "0.1.0",
    riskPosture: "touches_send_path",
    environments: { dev: true, staging: false, prod: false },
    duties: [],
    agentIds: ["campaign", "campaign-operator", "campaign_sweep"],
    taskKinds: ["campaign.plan"],
    eventKinds: ["campaign.plan.ran", "campaign.proposed", "campaign.roi_reported", "consent.recorded", "consent.withdrawn"],
    flags: ["ACKRA_ENABLE_CAMPAIGN_PLAN", "ACKRA_ENABLE_MARKETING_OPTIN", "ACKRA_ENABLE_COMMENT_TRIGGERS"],
    auditQuery: { agentIds: ["campaign", "campaign-operator", "campaign_sweep"] },
  },
];

export const AGENT_REGISTRY: readonly RegistryEntry[] = deepFreeze<RegistryEntry[]>(ENTRIES.map((e) => ({ ...e })));

/** The entry for a canvas agent id, or null for anything else (a system actor, an unplaced id). */
export function registryEntry(id: string): RegistryEntry | null {
  return AGENT_REGISTRY.find((e) => e.id === id) ?? null;
}
